import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Modal, message, Button } from 'antd';
import { RightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.css';
import axios from 'axios';
import { updateMetaTags } from '../../utils/metaUtils';

const { Title, Text } = Typography;

// 自定义专业图标组件
const CategoryIcon = ({ categoryCode }) => {
  // 根据分类代码返回对应的SVG图标
  switch(categoryCode) {
    case 'accounting':
      return (
        <div className={styles.customIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#1ED6FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="3" width="16" height="18" rx="2" />
            <line x1="8" y1="7" x2="16" y2="7" />
            <line x1="8" y1="11" x2="16" y2="11" />
            <line x1="8" y1="15" x2="12" y2="15" />
            <path d="M16 15h.01" />
            <path d="M16 19h.01" />
          </svg>
        </div>
      );
    case 'construction':
      return (
        <div className={styles.customIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#1ED6FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18" />
            <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
            <path d="M10 9h4" />
            <path d="M10 13h4" />
            <path d="M10 17h4" />
            <path strokeWidth="1" d="M8 21v-2.1L2 13l2-2 6 6v-2h2v6H8z" fill="rgba(30, 214, 255, 0.1)" />
          </svg>
        </div>
      );
    case 'economics':
      return (
        <div className={styles.customIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#1ED6FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="7" />
            <path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12" />
            <path d="M9 10h6" />
            <path d="M12 7v6" />
          </svg>
        </div>
      );
    case 'teacher':
      return (
        <div className={styles.customIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#1ED6FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l9 3v5c0 4.97-3.13 9.74-9 12-5.87-2.26-9-7.03-9-12V5l9-3z" />
            <path d="M17 9.5L12 14l-2-2" />
            <path d="M8 10.5l4-3 2 1.5L18 6" />
          </svg>
        </div>
      );
    default:
      return (
        <div className={styles.customIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#1ED6FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
        </div>
      );
  }
};

const Tiku = () => {
  const navigate = useNavigate();
  const [currentModal, setCurrentModal] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [defaultCategory, setDefaultCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
    updateMetaTags('tiku');
    
    // 检查是否有上次选择的分类
    const lastSelected = localStorage.getItem('lastSelectedCategory');
    if (lastSelected) {
      try {
        const { mainCategory, subCategory } = JSON.parse(lastSelected);
        setDefaultCategory(JSON.parse(lastSelected));
        // 如果有上次选择，自动导航到对应页面
        navigate(`/tiku/${mainCategory}/${subCategory}`);
      } catch (error) {
        console.error('解析上次选择的分类失败', error);
        // 出错时清除可能损坏的数据
        localStorage.removeItem('lastSelectedCategory');
      }
    }
  }, [navigate]);

  // 从后端获取分类数据
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/categories');
      if (response.data) {
        const { mainCategories, subCategories } = response.data;
        setCategories(mainCategories);
        setSubCategories(subCategories);
      }
    } catch (error) {
      message.error('获取分类数据失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    setCurrentModal(category.code);
  };

  const handleSubCategoryClick = (category, subCategory) => {
    setCurrentModal(null);
    
    // 保存用户选择到本地存储
    const categoryData = {
      mainCategory: category.code,
      subCategory: subCategory.code,
      mainCategoryName: category.name,
      subCategoryName: subCategory.name
    };
    
    localStorage.setItem('lastSelectedCategory', JSON.stringify(categoryData));
    
    // 发布一个自定义事件，通知其他组件默认分类已更新
    const event = new CustomEvent('categoryChanged', { detail: categoryData });
    window.dispatchEvent(event);
    
    // 跳转到对应分类页面
    navigate(`/tiku/${category.code}/${subCategory.code}`);
  };

  // 重置上次选择的分类
  const resetLastSelected = () => {
    localStorage.removeItem('lastSelectedCategory');
    setDefaultCategory(null);
    
    // 发布一个自定义事件，通知其他组件默认分类已重置
    const event = new CustomEvent('categoryChanged', { detail: null });
    window.dispatchEvent(event);
    
    message.success('已重置默认分类，下次访问将显示所有分类');
  };

  const currentCategory = categories.find(c => c.code === currentModal);

  return (
    <div className={styles.tikuContainer}>
      <div className={styles.header}>
        <Title level={2} className={styles.pageTitle}>
          职考专业切换
        </Title>
        <div className={styles.headerContent}>
          <Text className={styles.description}>
            系统收录各类职业资格考试真题及模拟试题，助您高效备考
          </Text>
          {defaultCategory && (
            <div className={styles.defaultCategoryInfo}>
              <InfoCircleOutlined />
              <Text className={styles.defaultCategoryText}>
                您的默认分类: {defaultCategory.mainCategoryName} - {defaultCategory.subCategoryName}
              </Text>
              <Button type="link" size="small" onClick={resetLastSelected}>
                取消默认
              </Button>
            </div>
          )}
        </div>
      </div>

      <Row gutter={[24, 24]} className={styles.cardContainer}>
        {categories.map(category => (
          <Col xs={24} sm={12} md={6} key={category._id}>
            <Card 
              className={styles.subjectCard}
              onClick={() => handleCategoryClick(category)}
            >
              <div className={styles.cardContent}>
                <CategoryIcon categoryCode={category.code} />
                <Title level={4}>{category.name}</Title>
                <Text>{category.description}</Text>
                <RightOutlined className={styles.arrowIcon} />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 子分类模态框 - 优化UI */}
      <Modal
        title={`${currentCategory?.name}题库分类`}
        open={!!currentModal}
        onCancel={() => setCurrentModal(null)}
        footer={null}
        width={800}
        centered
        className={styles.categoryModal}
      >
        <div className={styles.subCategoryGrid}>
          {subCategories[currentModal]?.map(sub => (
            <div 
              key={sub._id}
              className={styles.subCategoryBox}
              onClick={() => handleSubCategoryClick(currentCategory, sub)}
            >
              <div className={styles.boxContent}>
                <div className={styles.boxTitle}>{sub.name}</div>
                <div className={styles.boxIcon}>
                  <RightOutlined />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Tiku; 